import languages from 'constants/languages';
import { useTranslate } from 'hooks/useTranslate';
import i18n from 'i18n/config';
import { boardDelegationRoles } from 'modules/boards/constants/boardDelegation';
import { sindicationRefs } from 'modules/boards/constants/sindicationRefs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { MultiValue } from 'react-select';
import { State } from 'redux/initialState';

import { Partner, PartnerCategory, SelectorOption, Society } from 'types';
import { hasPartnersDecoupled } from 'utils/showDetails';

type Props = {
  currentPartner: Partner;
  actualSociety: Society;
  partnerCategories: PartnerCategory[];
};

export function useGetPartnerSelectedOptions({
  currentPartner,
  actualSociety,
  partnerCategories,
}: Props) {
  const { t } = useTranslate();
  const partners = useSelector((state: State) =>
    hasPartnersDecoupled() ? state.partners : actualSociety?.partners
  );
  const partnerRepresentants = useSelector(
    (state: State) => state.partnerRepresentants
  );

  const [selectedCategoriesOptions, setSelectedCategoriesOption] = useState<
    MultiValue<SelectorOption>
  >([]);
  const [selectedSindicationOption, setSelectedSindicationOption] =
    useState<SelectorOption>();
  const [selectedCivilStatusOption, setSelectedCivilStatusOption] =
    useState<SelectorOption>();
  const [selectedLanguageOption, setSelectedLanguageOption] =
    useState<SelectorOption>();

  useEffect(() => {
    if (currentPartner?.categories?.length) {
      const categories = partnerCategories
        ?.filter((category) =>
          currentPartner?.categories?.includes(category?._id)
        )
        .filter((category) => !category?.isDefault);

      setSelectedCategoriesOption(
        categories.map((category) => ({
          value: category?._id || '',
          label: i18n.t(category?.name) || '',
        }))
      );
    }
  }, [currentPartner, partnerCategories, i18n.language]);

  useEffect(() => {
    const representantPool =
      currentPartner?.sindicationRef === sindicationRefs.PARTNER
        ? partners
        : partnerRepresentants;

    const sindicationPartner = representantPool.find(
      (member) => member?.['_id'] === currentPartner?.sindication
    );
    if (sindicationPartner) {
      const option = {
        role: boardDelegationRoles.PARTNER,
        value: currentPartner?.sindication || '',
        label: `${sindicationPartner?.cif} | ${sindicationPartner?.name}`,
      };
      setSelectedSindicationOption(option);
    }
  }, [
    actualSociety,
    currentPartner,
    partnerCategories,
    partnerRepresentants,
    partners,
  ]);

  useEffect(() => {
    const civilStatusOption = {
      value: currentPartner?.civilStatus || '',
      label: t(currentPartner?.civilStatus) || t('SelectCivilStatus'),
    };
    const languageOption = {
      value: currentPartner?.language || languages.es.value,
      label: languages[currentPartner?.language || 'es']?.name,
    };

    setSelectedCivilStatusOption(civilStatusOption);
    setSelectedLanguageOption(languageOption);
  }, [currentPartner]); // eslint-disable-line

  return {
    data: {
      selectedCategoriesOptions,
      selectedSindicationOption,
      selectedCivilStatusOption,
      selectedLanguageOption,
    },
    isLoading: false,
    isError: false,
    actions: {
      setSelectedCategoriesOption,
      setSelectedSindicationOption,
      setSelectedCivilStatusOption,
      setSelectedLanguageOption,
    },
  };
}
